import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useState } from 'react';
import FilterChips from './FilterChips';
import SortBy from './SortBy';
import MultiSelectActions from './MultiSelectActions';
import { Facets, FacetsMobileBtn } from 'edcom-ui';
import { replaceParams } from '../../../services/content';
import styles from './SearchFilters.module.less';
import SelectedContentChips from './SelectedContentChips';
import SelectedContentFacet from './SelectedContentFacet';
import InteractiveWorksheetsFilter from './InteractiveWorksheetsFilter';
import { trackGA } from '../../../services/track';

const getChipFilters = (currentFilters = []) => {
  return currentFilters.filter(filter => filter.group !== 'contentType');
};

function SearchFilters({
  total = 0,
  allFacets = [],
  filter,
  currentFilters,
  urlPath,
  onFilter,
  selected = [],
  unSelect,
  showOnlySelected,
  setShowOnlySelected,
}) {
  const [open, setOpen] = useState(false);

  const [
    hasInteractiveWorksheetTooltip,
    setHasInteractiveWorksheetTooltip,
  ] = useState(false);

  const handleClickFacetMobileBtn = () => {
    setOpen(!open);
  };

  const handleClearFilters = () => {
    setOpen(false);
    onFilter('/resources/');
  };

  const handleClickFacet = async (e, facetHref) => {
    e.preventDefault();
    onFilter(facetHref);
  };

  const handleClickSort = sort => {
    const href = replaceParams(urlPath, { sort });
    onFilter(href);
  };

  const handleCheckInteractive = checked => {
    const href = replaceParams(urlPath, {
      filter: checked ? 'interactive' : '',
    });
    trackGA('Search', `interactive-only-filter-${checked}`);
    onFilter(href);
  };

  const handleShowInteractiveWorksheetTooltip = () => {
    setHasInteractiveWorksheetTooltip(!hasInteractiveWorksheetTooltip);
  };

  const interactive = urlPath.includes('filter=interactive');

  const renderTotalResults = (total, currentFilters) => {
    if (total === 1) return '1 result';
    const formattedTotal = total
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

    return currentFilters.length
      ? `${formattedTotal} filtered results`
      : `${formattedTotal} results`;
  };

  return (
    <>
      <div className={clsx(styles.overlay, open && styles.open)} />
      <div className={styles.container}>
        <FacetsMobileBtn onClick={handleClickFacetMobileBtn} />
        <div className={styles.numberWrapperMobile}>
          <span className={clsx(styles.numberText, styles.numberMobile)}>
            {renderTotalResults(total, currentFilters)}
          </span>
        </div>
        <div className={styles.topFiltersWrapper}>
          <div className={styles.topFilters}>
            <span className={styles.numberWrapper}>
              <span className={styles.numberText}>
                {renderTotalResults(total, currentFilters)}
              </span>
            </span>
            <FilterChips
              filters={getChipFilters(currentFilters)}
              clickFilterChip={onFilter}
            />
            {selected.length > 0 && (
              <SelectedContentChips selected={selected} unSelect={unSelect} />
            )}
          </div>
        </div>
        {selected.length > 0 && <MultiSelectActions selected={selected} />}
        {filter.contentType === 'worksheets' && (
          <InteractiveWorksheetsFilter
            checked={interactive}
            selectCallback={handleCheckInteractive}
            hasTooltip={hasInteractiveWorksheetTooltip}
            showTooltip={handleShowInteractiveWorksheetTooltip}
          />
        )}
        <SortBy selectedSort={filter.sort} clickSort={handleClickSort} />
      </div>
      <div className={styles.searchFilterContainer}>
        {selected.length > 0 && (
          <SelectedContentFacet
            selected={selected}
            unSelect={unSelect}
            showOnlySelected={showOnlySelected}
            setShowOnlySelected={setShowOnlySelected}
          />
        )}
        <Facets
          allFacets={allFacets}
          onClick={handleClickFacet}
          open={open}
          closeFilterWrap={() => setOpen(false)}
          clearFilters={handleClearFilters}
        />
      </div>
    </>
  );
}

SearchFilters.displayName = 'SearchFilters';
SearchFilters.propTypes = {
  total: PropTypes.number,
  allFacets: PropTypes.array,
  filter: PropTypes.object,
  currentFilters: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string,
      group: PropTypes.string,
      href: PropTypes.string,
    })
  ),
  urlPath: PropTypes.string,
  /** Event handler for responding to filter changes **/
  onFilter: PropTypes.func,
  selected: PropTypes.array,
  unSelect: PropTypes.func,
  showOnlySelected: PropTypes.bool,
  setShowOnlySelected: PropTypes.func,
};

export default SearchFilters;
